import useGetMyAccountAccountingIntegrations from '@/api/queries/me/useGetMyAccountAccountingIntegrations';
import { Invoice } from '@/api/queries/project/useGetProjectInvoices';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { AnchorButton, Button, useToastContext } from '@payaca/ambrosia';

import { PaymentsPermissions } from '@payaca/permissions/payments/payments.permissions';
import { pushInvoiceToAccountingIntegrations } from '@payaca/store/invoices/invoicesActions';
import UntitledIcon from '@payaca/untitled-icons';
import { Dispatch, FC, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import RecordPaymentModal from '../recordPaymentModal/RecordPaymentModal';
import { InvoiceCard } from './components/InvoiceCard';

type ModalState = {
  activeModal: 'recordPayment' | null;
};

type ModalAction =
  | {
      type: 'openRecordPaymentModal';
    }
  | {
      type: 'closeModal';
    };

const modalReducer = (state: ModalState, action: ModalAction): ModalState => {
  switch (action.type) {
    case 'openRecordPaymentModal':
      return {
        activeModal: 'recordPayment',
      };
    case 'closeModal':
      return { activeModal: null };
    default:
      return state;
  }
};

const useInvoiceActionElements = (
  invoice: Invoice,
  modalDispatch: Dispatch<ModalAction>
) => {
  const dispatch = useDispatch();
  const actionElements: React.ReactNode[] = [];
  const { pushSuccess } = useToastContext();

  const hasRecordPaymentPermission = useUserHasPermission({
    permissions: [PaymentsPermissions.RECORD_PAYMENT],
  });

  const { data: accountingIntegrations } =
    useGetMyAccountAccountingIntegrations();

  const requiresQuickbooksSync =
    accountingIntegrations?.quickbooks?.accounts.length &&
    !invoice.accountingIntegrationSyncData?.quickbooks?.invoiceIdentifier;

  const requiresXeroSync =
    accountingIntegrations?.xero?.accounts.length &&
    !invoice.accountingIntegrationSyncData?.xero?.invoiceIdentifier;

  const requiresSync = requiresXeroSync || requiresQuickbooksSync;

  if (!invoice.sentAt && !invoice.voidedAt) {
    actionElements.push(
      <AnchorButton
        as={Link}
        to={`/projects/${invoice.project.id}/invoices/${invoice.id}/edit/send`}
        className="flex items-center gap-2"
        linkColour="dark"
        variant="link"
      >
        <UntitledIcon name="mail-02.3" className="w-4 h-4" />
        <span>Send</span>
      </AnchorButton>
    );
  }

  if (
    !!invoice.sentAt &&
    !invoice.voidedAt &&
    !invoice.isGrantInvoice &&
    invoice.outstandingValue.value > 0 &&
    hasRecordPaymentPermission
  ) {
    actionElements.push(
      <Button
        variant="link"
        onClick={() =>
          modalDispatch({
            type: 'openRecordPaymentModal',
          })
        }
        linkColour="dark"
      >
        <UntitledIcon name="banknote" className="w-4 h-4" />
        <span>Record payment</span>
      </Button>
    );
  }

  if (!!invoice?.sentAt && !invoice.voidedAt && requiresSync) {
    actionElements.push(
      <Button
        variant="link"
        linkColour="dark"
        onClick={() => {
          dispatch(
            pushInvoiceToAccountingIntegrations.request({
              invoiceId: Number(invoice.id),
            })
          );
          pushSuccess('Attempting to push invoice to accounting software');
        }}
      >
        <UntitledIcon name="refresh-cw-01" className="w-4 h-4" />
        <span>Push to accounting software</span>
      </Button>
    );
  }

  // todo implement download action - not initially necessary

  return actionElements;
};

export const InvoiceCardActions: FC<{
  invoice: Invoice;
  onRecordPaymentSuccess?: () => void;
}> = ({ invoice, onRecordPaymentSuccess }) => {
  const [modalState, modalStateDispatch] = useReducer(modalReducer, {
    activeModal: null,
  });
  const actionElements = useInvoiceActionElements(invoice, modalStateDispatch);

  return (
    <>
      <InvoiceCard.Actions>
        <InvoiceCard.Actions.Secondary>
          {actionElements.map((actionElement, i) => {
            return (
              <InvoiceCard.Actions.Secondary.Action key={i}>
                {actionElement}
              </InvoiceCard.Actions.Secondary.Action>
            );
          })}
        </InvoiceCard.Actions.Secondary>
        <InvoiceCard.Actions.PrimaryAction>
          <AnchorButton
            as={Link}
            to={`/projects/${invoice?.project.id}/invoices/${invoice.id}${
              invoice.sentAt ? '' : '/edit'
            }`}
            linkColour="dark"
            variant="link"
          >
            View details
          </AnchorButton>
        </InvoiceCard.Actions.PrimaryAction>
      </InvoiceCard.Actions>
      <RecordPaymentModal
        dealId={Number(invoice.project.id)}
        paymentTarget={{
          invoiceId: Number(invoice.id),
        }}
        isOpen={modalState.activeModal === 'recordPayment'}
        onClose={() => modalStateDispatch({ type: 'closeModal' })}
        recordPaymentCallback={onRecordPaymentSuccess}
        confirmPaymentCallback={onRecordPaymentSuccess}
      />
    </>
  );
};
